// admin-ui/src/features/c/components/AttributePanel.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const WIND_PROFILE_KEY = 'wind_profile';
const WIND_PROFILE_LANG = 'und' as const;
const WIND_INFO_KEY = 'wind_profile_info';

// LiveWind: Key + feste ID 107 (dein Datensatz)
const LIVEWIND_KEY = 'livewind_station';
const LIVEWIND_ATTR_ID = 107;

// Google Photos (read-only) – Attributdefinition 17
const GPHOTOS_KEY = 'photos';
const GPHOTOS_ATTR_ID = 17;

// ⬇️ Attribute-Keys, die in der Oberfläche nicht mehr angezeigt werden sollen
const HIDDEN_KEYS = new Set<string>([
  WIND_PROFILE_KEY, // altes Wind/Schwell-Profil
  'wind_n',
  'wind_s',
  'wind_e',

  // alte Foto-Felder mit exakten Keys
  'photo_1',
  'photo_2',
  'photo_3',
  'photo_4',
  'photo_5',
  'photos height',
  'photos html attributions',
  'photos photo reference',
  'photos width',

  // technisches Feld, nur für Debug sinnvoll
  'plus_code.compound_code',
]);

// ⬇️ Attribute-Prefixes, die ausgeblendet werden sollen (altes Google-Foto-System)
const HIDDEN_PREFIXES = ['photo_', 'photos_', 'google_photo_', 'photos ', 'photos['];

type Lang = 'de' | 'en' | 'it' | 'fr' | 'hr';
const LANGS: Lang[] = ['de', 'en', 'it', 'fr', 'hr'];

type AttributeDef = {
  id: number;
  key: string;
  input_type?: string | null;
  multilingual?: boolean | null;
  options?: any;
  default_value?: any;
  name_de?: string | null;
  name_en?: string | null;
  name_it?: string | null;
  name_fr?: string | null;
  name_hr?: string | null;
  description_de?: string | null;
  description_en?: string | null;
  description_it?: string | null;
  description_fr?: string | null;
  description_hr?: string | null;
};

type LocationValue = {
  attribute_id: number;
  language_code: string | null; // "und" oder null
  value_json: any;
};

type LivewindSuggestion = {
  id: string;
  name: string | null;
  distance_m?: number | null;
};

/* ---------- Helpers ---------- */
const toStr = (v: unknown): string =>
  v === null || v === undefined ? '' : typeof v === 'string' ? v : String(v);

const toNumOrEmpty = (v: unknown): number | '' => {
  if (v === '' || v === null || v === undefined) return '';
  if (typeof v === 'number') return v;
  const n = Number(v as any);
  return Number.isFinite(n) ? n : '';
};

// aus value_json einen gut lesbaren Text ziehen (auch für Objekte)
const extractTextLike = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
    return String(val);

  // Arrays: nur Meta-Wrapper entpacken. Strukturierte Arrays (z. B. Google Photos) bleiben unverändert.
  if (Array.isArray(val)) {
    if (isMetaWrappedArray(val)) return extractTextLike(val[0]);
    return '';
  }
if (typeof val === 'object') {
    if ('text' in val) return String((val as any).text ?? '');
    if ('value' in val) return String((val as any).value ?? '');
    if ('id' in val) return String((val as any).id ?? '');
    if ('name' in val) return String((val as any).name ?? '');
    if ('label' in val) return String((val as any).label ?? '');
  }

  return '';
};


const isMetaObject = (x: any): boolean => {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
  return (
    typeof (x as any).ai === 'boolean' ||
    typeof (x as any).by === 'string' ||
    typeof (x as any).ts === 'string' ||
    typeof (x as any).source === 'string'
  );
};

const isMetaWrappedArray = (v: any): boolean => {
  // Wrapper für skalare Werte: [value, meta] oder [value, meta, ...]
  if (!Array.isArray(v) || v.length < 2) return false;

  const primary = v[0];
  const looksScalar =
    primary === null ||
    primary === undefined ||
    typeof primary === 'string' ||
    typeof primary === 'number' ||
    typeof primary === 'boolean';
  if (!looksScalar) return false;

  for (let i = 1; i < v.length; i++) {
    if (isMetaObject(v[i])) return true;
  }
  return false;
};

const extractPrimary = (v: any) => (isMetaWrappedArray(v) ? v[0] : v);

const extractMeta = (v: any) => {
  if (!isMetaWrappedArray(v)) return null;
  for (let i = 1; i < v.length; i++) {
    if (isMetaObject(v[i])) return v[i];
  }
  return null;
};

const makeMeta = (ai: boolean) => ({
  ai,
  by: ai ? 'w2h-ai' : 'w2h-admin',
  ts: new Date().toISOString(),
});

const wrapScalarWithMeta = (value: any, meta: any) => {
  // Nur für echte Skalare (string/number/boolean)
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [value, meta];
  }
  return value;
};

const withAiFlag = (value_json: any, ai: boolean) => {
  const primary = extractPrimary(value_json);
  const meta = { ...(extractMeta(value_json) || {}), ...makeMeta(ai) };
  return wrapScalarWithMeta(primary, meta);
};

const normalizeFirstValue = (v: any) => {
  // Nur Meta-Wrapper entpacken (nicht strukturierte Arrays wie z. B. Google Photos)
  if (isMetaWrappedArray(v)) return v[0];
  return v;
};

const normalizeBool = (v: any): boolean => {
  const x = normalizeFirstValue(v);
  if (typeof x === 'boolean') return x;
  if (typeof x === 'number') return x !== 0;
  if (typeof x === 'string') {
    const s = x.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'ja', 'on'].includes(s)) return true;
    if (['0', 'false', 'no', 'n', 'nein', 'off'].includes(s)) return false;
  }
  return !!x;
};

function resolveLang(): Lang {
  if (typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search).get('lang') as Lang | null;
    if (q && LANGS.includes(q)) return q;
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const base = (navigator.language || 'de').split('-')[0] as Lang;
    if (LANGS.includes(base)) return base;
  }
  return 'de';
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
    gap: 12,
  } as React.CSSProperties,
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 8,
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  } as React.CSSProperties,
  img: {
    width: '100%',
    height: 180,
    objectFit: 'cover',
    borderRadius: 8,
    display: 'block',
    background: '#f3f4f6',
  } as React.CSSProperties,
  cap: { fontSize: 12, color: '#6b7280', marginTop: 4 } as React.CSSProperties,
  link: { color: '#1d4ed8', fontSize: 12 } as React.CSSProperties,
  overlay: {
    position: 'fixed' as const,
    inset: '0',
    background: 'rgba(0,0,0,.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 200000,
  },
  overlayImg: {
    maxHeight: '90vh',
    maxWidth: '90vw',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0,0,0,.5)',
  } as React.CSSProperties,
  closeBtn: {
    position: 'fixed' as const,
    top: 16,
    right: 16,
    background: 'rgba(255,255,255,.9)',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 13,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    zIndex: 200001,
  },
};

// LiveWind consistent erkennen
const isLivewindDef = (a: AttributeDef | undefined | null) =>
  !!a && (a.key === LIVEWIND_KEY || a.id === LIVEWIND_ATTR_ID);

// Google Photos consistent erkennen
const isGPhotosDef = (a: AttributeDef | undefined | null) =>
  !!a && (a.key === GPHOTOS_KEY || a.id === GPHOTOS_ATTR_ID);

// Boolean immer UND (sprachunabhängig) – unabhängig vom multilingual-Flag
const isBooleanType = (a: AttributeDef | undefined | null) => {
  const t = ((a?.input_type || '') + '').toLowerCase();
  return t === 'boolean' || t === 'checkbox';
};

const isMLTextType = (a: AttributeDef | undefined | null) => {
  const t = ((a?.input_type || '') + '').toLowerCase();
  return !!a?.multilingual && (t === 'text' || t === 'textarea') && !isBooleanType(a);
};

/* ---------- Google Photos (read-only) ---------- */
type GPhoto = {
  height?: number;
  width?: number;
  html_attributions?: string[];
  photo_reference: string;
};

function normalizePhotos(val: any): GPhoto[] {
  // Google Photos kommen als Array aus Objekten
  if (!val) return [];
  if (Array.isArray(val)) return val as GPhoto[];

  // Falls versehentlich als String JSON
  if (typeof val === 'string') {
    try {
      const arr = JSON.parse(val);
      return Array.isArray(arr) ? (arr as GPhoto[]) : [];
    } catch {
      return [];
    }
  }

  // Falls als Wrapper {items:[...]}
  if (Array.isArray((val as any)?.items)) return (val as any).items as GPhoto[];

  return [];
}

/** Klickbare Fotos via Portal; Position relativ zum Dokument (scroll-synchron). */
function PhotosGrid({ value }: { value: any }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const photos = normalizePhotos(value).slice(0, 12);

  useEffect(() => {
    if (!hostRef.current) return;
    const el = hostRef.current;

    const update = () => {
      const r = el.getBoundingClientRect();
      const left = r.left + window.scrollX;
      const top = r.top + window.scrollY;
      setRect(new DOMRect(left, top, r.width, r.height));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const proxyUrl = (ref: string, maxWidth: number) =>
    `/api/gphoto?photoreference=${encodeURIComponent(ref)}&maxwidth=${encodeURIComponent(
      String(maxWidth)
    )}`;

  const rows = Math.ceil((photos.length || 0) / 2);
  const rowH = 180 + 8 + 28 + 16;

  return (
    <div>
      <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 6 }}>
        {photos.length ? `${photos.length} Fotos` : 'Keine Fotos vorhanden'} · Key geschützt
      </div>

      <div ref={hostRef} style={{ minHeight: rows ? rows * rowH : 40 }}>
        {!photos.length && <div style={{ fontSize: 13, opacity: 0.7 }}>Keine Fotos</div>}
      </div>

      {mounted &&
        rect &&
        photos.length > 0 &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              left: rect.left,
              top: rect.top,
              width: rect.width,
              zIndex: 100000,
              pointerEvents: 'auto',
            }}
          >
            <div style={styles.grid}>
              {photos.map((p, i) => {
                const thumb = proxyUrl(p.photo_reference, 400);
                const full = proxyUrl(p.photo_reference, 1600);
                return (
                  <a
                    key={i}
                    href={full}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      setPreviewSrc(full);
                    }}
                    style={{ ...styles.card, textDecoration: 'none', display: 'block' }}
                    title="Groß anzeigen"
                  >
                    <img
                      src={thumb}
                      alt={`Foto ${i + 1}`}
                      style={styles.img}
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.opacity = '0.35';
                      }}
                    />
                    <div style={styles.cap}>
                      {(p.width ?? '?')}×{p.height ?? '?'}
                    </div>
                    {p.html_attributions?.length ? (
                      <div
                        style={{ ...styles.link, marginTop: 4 }}
                        dangerouslySetInnerHTML={{ __html: p.html_attributions[0] }}
                      />
                    ) : null}
                  </a>
                );
              })}
            </div>
          </div>,
          document.body
        )}

      {mounted &&
        previewSrc &&
        createPortal(
          <>
            <div
              style={styles.overlay}
              onClick={() => setPreviewSrc(null)}
              role="dialog"
              aria-modal="true"
            >
              <img
                src={previewSrc}
                alt="Preview"
                style={styles.overlayImg}
                onClick={(e) => e.stopPropagation()}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
            <button
              type="button"
              style={styles.closeBtn as any}
              onClick={() => setPreviewSrc(null)}
            >
              Schließen ⎋
            </button>
          </>,
          document.body
        )}
    </div>
  );
}

/* ---------- Haupt-Component ---------- */
export default function AttributePanel({ locationId }: { locationId: number }) {
  const [attrs, setAttrs] = useState<AttributeDef[]>([]);
  const [values, setValues] = useState<LocationValue[]>([]);
  const [activeLang, setActiveLang] = useState<Lang>(resolveLang());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Zusatz-UI
  const [addAttrId, setAddAttrId] = useState<number | ''>('');
  const [addValue, setAddValue] = useState<any>('');
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] =
    useState<'text' | 'number' | 'boolean' | 'select' | 'multiselect' | 'json'>('text');
  const [newLabelDe, setNewLabelDe] = useState('');
  const [newOptionsJson, setNewOptionsJson] = useState('');

  // LiveWind: Meteostat-Vorschlag
  const [livewindSuggestion, setLivewindSuggestion] = useState<LivewindSuggestion | null>(
    null
  );
  const [livewindSuggestLoading, setLivewindSuggestLoading] = useState(false);
  const [livewindSuggestError, setLivewindSuggestError] = useState<string | null>(null);

  // KI-Translate UX
  const [translateBusyAttr, setTranslateBusyAttr] = useState<number | null>(null);
  const [translateErr, setTranslateErr] = useState<string | null>(null);
  const [translateOverwrite, setTranslateOverwrite] = useState(false);

  // ---------- LOAD ----------
  async function load() {
    try {
      setLoading(true);
      setError(null);

      // 1) Definitions einmal laden (lang für Labels/Descriptions)
      const resDefs = await fetch(
        `/api/location-attributes?location_id=${locationId}&lang=${activeLang}`
      );
      if (!resDefs.ok) throw new Error(await resDefs.text());
      const jsonDefs = await resDefs.json();

      const defs: AttributeDef[] = (jsonDefs.definitions || []).map((d: any) => ({
        id: Number(d.attribute_id ?? d.id ?? 0),
        key: toStr(d.key),
        input_type: d.input_type ?? 'text',
        multilingual: !!d.multilingual,
        options: d.options ?? null,
        default_value: d.default_value ?? null,
        name_de: d.name_de ?? null,
        name_en: d.name_en ?? null,
        name_it: d.name_it ?? null,
        name_fr: d.name_fr ?? null,
        name_hr: d.name_hr ?? null,
        description_de: d.description_de ?? null,
        description_en: d.description_en ?? null,
        description_it: d.description_it ?? null,
        description_fr: d.description_fr ?? null,
        description_hr: d.description_hr ?? null,
      }));

      const defById = new Map<number, AttributeDef>();
      defs.forEach((d) => defById.set(d.id, d));

      const hiddenIds = new Set<number>(
        defs
          .filter(
            (d) =>
              HIDDEN_KEYS.has(d.key) ||
              HIDDEN_PREFIXES.some((prefix) => d.key.startsWith(prefix))
          )
          .map((d) => d.id)
      );

      // Wert-Coalescing: bool/number/option/text/json sauber übernehmen
      const coalesceValue = (v: any) => {
        if (v?.attribute_id == null) return null;

        const aid = Number(v.attribute_id);
        const def = defById.get(aid);

        // ✅ Google Photos: IMMER value_json unverändert (Array behalten!)
        if (def && isGPhotosDef(def)) {
          return v.value_json ?? null;
        }

        // LiveWind bevorzugt value_text (Stations-ID)
        if (aid === LIVEWIND_ATTR_ID) {
          return normalizeFirstValue(v.value_text ?? v.value ?? v.value_json ?? null);
        }

        if (v.value_bool !== null && v.value_bool !== undefined) return v.value_bool;
        if (v.value_number !== null && v.value_number !== undefined) return v.value_number;
        if (v.value_option !== null && v.value_option !== undefined) return v.value_option;

        return normalizeFirstValue(v.value_text ?? v.value_json ?? v.value ?? null);
      };

      // 2) Values für UND + alle Sprachen parallel laden
      const langsToLoad = [WIND_PROFILE_LANG, ...LANGS]; // ['und','de','en','it','fr','hr']
      const results = await Promise.all(
        langsToLoad.map(async (l) => {
          const r = await fetch(`/api/location-attributes?location_id=${locationId}&lang=${l}`);
          if (!r.ok) return { lang: l, json: { values: [] } as any };
          const j = await r.json().catch(() => ({ values: [] }));
          return { lang: l, json: j };
        })
      );

      let vals: LocationValue[] = results
        .flatMap(({ lang, json }) =>
          (json.values || []).map((v: any) => ({
            attribute_id: Number(v.attribute_id),
            language_code: v.language_code ?? (lang === WIND_PROFILE_LANG ? WIND_PROFILE_LANG : lang),
            value_json: coalesceValue(v),
          }))
        )
        .filter((v: any) => !hiddenIds.has(v.attribute_id));

      const visibleDefs = defs.filter(
        (d) =>
          !HIDDEN_KEYS.has(d.key) &&
          !HIDDEN_PREFIXES.some((prefix) => d.key.startsWith(prefix))
      );

      // LiveWind: falls aus UND kommt → in null-Bucket mappen (damit Mono-Handling + save() sauber bleiben)
      const livewindDef = visibleDefs.find((d) => isLivewindDef(d));
      if (livewindDef) {
        const undKey = `${livewindDef.id}|${WIND_PROFILE_LANG}`;
        const foundUnd = vals.find(
          (x) => `${x.attribute_id}|${x.language_code ?? '__'}` === undKey
        );
        if (foundUnd) {
          vals = vals.filter((x) => !(`${x.attribute_id}|${x.language_code ?? '__'}` === undKey));
          vals.push({ ...foundUnd, language_code: null });
        }
      }

      // Dedupe by (attribute_id|lang)
      const dedupe = new Map<string, LocationValue>();
      for (const v of vals) {
        dedupe.set(`${v.attribute_id}|${v.language_code ?? '__'}`, v);
      }
      vals = Array.from(dedupe.values());

      setAttrs(visibleDefs);
      setValues(vals);
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Laden der Attribute.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, activeLang]);

  // LiveWind: passende Meteostat-Station vorschlagen
  useEffect(() => {
    let cancelled = false;

    async function fetchSuggestion() {
      try {
        setLivewindSuggestLoading(true);
        setLivewindSuggestError(null);
        setLivewindSuggestion(null);

        const res = await fetch(`/api/meteostat-suggest?location_id=${locationId}`);
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(txt || `HTTP ${res.status}`);
        }
        const j = await res.json();
        if (cancelled) return;

        const id = j.id != null ? String(j.id) : '';
        const name = j.name != null ? String(j.name) : null;
        const distance_m =
          typeof j.distance_m === 'number' ? j.distance_m : j.distance ?? null;

        if (!id) {
          setLivewindSuggestion(null);
          setLivewindSuggestError('Keine Station gefunden');
        } else {
          setLivewindSuggestion({ id, name, distance_m });
        }
      } catch (err: any) {
        if (cancelled) return;
        console.warn('[meteostat-suggest] failed', err);
        setLivewindSuggestion(null);
        setLivewindSuggestError('Keine Meteostat-Station gefunden');
      } finally {
        if (!cancelled) setLivewindSuggestLoading(false);
      }
    }

    fetchSuggestion();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const byAttrLang = useMemo(() => {
    const map = new Map<string, LocationValue>();
    for (const v of values) {
      map.set(`${v.attribute_id}|${v.language_code ?? '__'}`, v);
    }
    return map;
  }, [values]);

  function setVal(attr: AttributeDef, lang: string | null, value_json: any) {
    const row: LocationValue = { attribute_id: attr.id, language_code: lang, value_json };
    setValues((prev) => {
      const copy = [...prev];
      const key = `${attr.id}|${lang ?? '__'}`;
      const idx = copy.findIndex(
        (v) => `${v.attribute_id}|${v.language_code ?? '__'}` === key
      );
      if (idx >= 0) copy[idx] = row;
      else copy.push(row);
      return copy;
    });
  }

  
  function setValManual(attr: AttributeDef, lang: string | null, raw: any) {
    // Manuelle Änderung: ai=false (nur für skalare Wrapper). Strukturierte JSON/Arrays bleiben unverändert.
    if (isGPhotosDef(attr)) return; // Google Photos sind read-only
    const type = ((attr.input_type || 'text') + '').toLowerCase();
    if (type === 'json') {
      setVal(attr, lang, raw);
      return;
    }
    setVal(attr, lang, withAiFlag(raw, false));
  }
async function translateFromDe(attr: AttributeDef) {
    try {
      setTranslateErr(null);
      setTranslateBusyAttr(attr.id);

      const rawDe = byAttrLang.get(`${attr.id}|de`)?.value_json;
      const src = extractTextLike(rawDe).trim();
      if (!src) {
        setTranslateErr('Kein deutscher Text vorhanden.');
        return;
      }

      const res = await fetch('/api/c/translate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_language: 'de',
          target_languages: ['en', 'fr', 'it', 'hr'],
          text: src,
          context: {
            entity: 'location_value',
            attribute_key: attr.key,
            attribute_id: attr.id,
          },
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      const j = await res.json().catch(() => ({} as any));
      const t = j?.translations || {};

      (['en', 'fr', 'it', 'hr'] as const).forEach((l) => {
        const proposed = toStr(t[l]).trim();
        if (!proposed) return;

        const existing = toStr(byAttrLang.get(`${attr.id}|${l}`)?.value_json).trim();
        if (existing && !translateOverwrite) return;

        setVal(attr, l, withAiFlag(proposed, true));
      });
    } catch (e: any) {
      setTranslateErr(e?.message || 'Übersetzung fehlgeschlagen.');
    } finally {
      setTranslateBusyAttr(null);
    }
  }

  async function save() {
    try {
      setSaving(true);
      setError(null);

      const valuesByLang: Record<string, Record<number, any>> = {
        [WIND_PROFILE_LANG]: {},
        de: {},
        en: {},
        fr: {},
        it: {},
        hr: {},
      };

      for (const a of attrs) {
        // Google Photos (Attribut 17) sind read-only und dürfen durch Save niemals überschrieben/geleert werden
        if (isGPhotosDef(a)) continue;
        const type = (a.input_type || 'text').toLowerCase();

        // ✅ ROBUST: Google Photos NIE mitspeichern (read-only)
        if (isGPhotosDef(a)) continue;

        // wind_profile immer UND
        if (a.key === WIND_PROFILE_KEY) {
          const v = byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
          if (v !== undefined) valuesByLang[WIND_PROFILE_LANG][a.id] = v ?? null;
          continue;
        }

        // Boolean immer UND
        if (type === 'boolean' || type === 'checkbox') {
          const raw =
            byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json ??
            byAttrLang.get(`${a.id}|__`)?.value_json;
          if (raw !== undefined) valuesByLang[WIND_PROFILE_LANG][a.id] = normalizeBool(raw);
          continue;
        }

        // Mehrsprachig (nur text/textarea)
        if (isMLTextType(a)) {
          for (const l of LANGS) {
            const v = byAttrLang.get(`${a.id}|${l}`)?.value_json;
            if (v !== undefined) valuesByLang[l][a.id] = v ?? null;
          }
          continue;
        }

        // Mono (null/UND) → wir speichern als UND
        const v =
          byAttrLang.get(`${a.id}|__`)?.value_json ??
          byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
        if (v !== undefined) valuesByLang[WIND_PROFILE_LANG][a.id] = v ?? null;
      }

      const putLang = async (lang: string, valuesObj: Record<number, any>) => {
        const keys = Object.keys(valuesObj);
        if (!keys.length) return;

        const res = await fetch('/api/location-attributes', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location_id: locationId, lang, values: valuesObj }),
        });
        if (!res.ok) throw new Error(await res.text());
      };

      await putLang(WIND_PROFILE_LANG, valuesByLang[WIND_PROFILE_LANG]);
      for (const l of LANGS) {
        await putLang(l, valuesByLang[l]);
      }

      await load();
      alert('Attribute gespeichert');
    } catch (e: any) {
      const msg = e?.message || 'Fehler beim Speichern.';
      setError(msg);
      alert(msg);
    } finally {
      setSaving(false);
    }
  }

  const labelOf = (a: AttributeDef) => {
    const l = activeLang;
    const byLang =
      (l === 'de' ? a.name_de : null) ||
      (l === 'en' ? a.name_en : null) ||
      (l === 'it' ? a.name_it : null) ||
      (l === 'fr' ? a.name_fr : null) ||
      (l === 'hr' ? a.name_hr : null);
    return toStr(byLang || a.name_de || a.name_en || a.key);
  };

  function asOptions(a: AttributeDef): Array<{ value: string; label: string }> {
    const raw = a.options;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      if (raw.length && typeof raw[0] === 'string')
        return raw.map((s: string) => ({ value: s, label: s }));
      return raw.map((o: any) => ({
        value: toStr(o?.value ?? o?.val ?? ''),
        label: toStr(o?.label_de ?? o?.label ?? o?.value ?? ''),
      }));
    }
    return [];
  }

  const addableDefs = useMemo(() => {
    return attrs
      .filter((a) => {
        const type = (a.input_type || 'text').toLowerCase();

        // ✅ photos ist read-only → nie über "Hinzufügen"
        if (isGPhotosDef(a)) return false;

        // wind_profile → nur wenn noch kein UND-Wert
        if (a.key === WIND_PROFILE_KEY) {
          return !byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`);
        }

        // LiveWind (Mono) wird ausschließlich über den Spezialblock gepflegt
        if (isLivewindDef(a)) return false;

        const hasAny = values.some((v) => v.attribute_id === a.id);

        // Boolean / ML / Mono: sobald irgendwo vorhanden, nicht addable
        if (type === 'boolean' || type === 'checkbox') return !hasAny;
        if (isMLTextType(a)) return !hasAny;
        return !hasAny;
      })
      .sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
  }, [attrs, byAttrLang, values, activeLang]);

  function renderAddValueInput(def?: AttributeDef) {
    if (!def) return null;
    const type = (def.input_type || 'text').toLowerCase();

    // ML-Textfelder → wir setzen beim "Hinzufügen" immer DE als Start
    if (isMLTextType(def)) {
      return type === 'textarea' ? (
        <textarea
          value={toStr(addValue)}
          onChange={(e) => setAddValue(e.target.value)}
          style={{
            width: '100%',
            padding: '9px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            minHeight: 84,
          }}
        />
      ) : (
        <input
          value={toStr(addValue)}
          onChange={(e) => setAddValue(e.target.value)}
          style={{
            width: '100%',
            padding: '9px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
          }}
        />
      );
    }

    switch (type) {
      case 'boolean':
      case 'checkbox':
        return (
          <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={normalizeBool(addValue)}
              onChange={(e) => setAddValue(e.target.checked)}
            />
            <span>Wert setzen</span>
          </label>
        );
      case 'number':
        return (
          <input
            type="number"
            value={toNumOrEmpty(addValue)}
            onChange={(e) =>
              setAddValue(e.target.value === '' ? '' : Number(e.target.value))
            }
            style={{
              width: 180,
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />
        );
      case 'select': {
        const opts = asOptions(def);
        return (
          <select
            value={toStr(addValue)}
            onChange={(e) => setAddValue(e.target.value)}
            style={{
              width: 240,
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          >
            <option value="">– wählen –</option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );
      }
      case 'json':
        return (
          <textarea
            value={(() => {
              try {
                return JSON.stringify(addValue ?? null, null, 2);
              } catch {
                return toStr(addValue);
              }
            })()}
            onChange={(e) => {
              try {
                setAddValue(JSON.parse(e.target.value));
              } catch {
                setAddValue(e.target.value);
              }
            }}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              minHeight: 84,
            }}
          />
        );
      case 'textarea':
        return (
          <textarea
            value={toStr(addValue)}
            onChange={(e) => setAddValue(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              minHeight: 84,
            }}
          />
        );
      default:
        return (
          <input
            value={toStr(addValue)}
            onChange={(e) => setAddValue(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />
        );
    }
  }

  function handleAddExisting() {
    if (addAttrId === '') return;
    const def = attrs.find((a) => a.id === addAttrId);
    if (!def) return;

    // Guard: Attribut existiert bereits irgendwo → nicht nochmal hinzufügen
    const hasAny = values.some((v) => v.attribute_id === def.id);
    if (hasAny) {
      alert('Dieses Attribut ist für diesen Marker bereits vorhanden.');
      return;
    }

    const type = (def.input_type || 'text').toLowerCase();

    if (def.key === WIND_PROFILE_KEY) {
      setValManual(def, WIND_PROFILE_LANG, addValue === '' ? null : addValue);
      setAddAttrId('');
      setAddValue('');
      return;
    }

    // Boolean → UND
    if (type === 'boolean' || type === 'checkbox') {
      setValManual(def, WIND_PROFILE_LANG, normalizeBool(addValue));
      setAddAttrId('');
      setAddValue('');
      return;
    }

    // ML-Text: Start in DE
    if (isMLTextType(def)) {
      setValManual(def, 'de', addValue === '' ? null : addValue);
      setAddAttrId('');
      setAddValue('');
      return;
    }

    // Mono → null
    setValManual(def, null, addValue === '' ? null : addValue);
    setAddAttrId('');
    setAddValue('');
  }

  async function handleCreateNew() {
    try {
      if (!newKey.trim() || !newLabelDe.trim()) {
        alert('Bitte Key und Deutsches Label angeben.');
        return;
      }
      let options: any = null;
      if (newOptionsJson.trim()) {
        try {
          options = JSON.parse(newOptionsJson);
        } catch {
          alert('Options-JSON ist ungültig.');
          return;
        }
      }

      const res = await fetch('/api/attribute-definitions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: newKey.trim(),
          type: newType,
          name_de: newLabelDe.trim(),
          multilingual: false,
          options,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || res.statusText);

      await load();
      const createdId = Number(j?.item?.id || j?.item?.attribute_id);
      if (Number.isFinite(createdId)) setAddAttrId(createdId);
      alert('Attributdefinition angelegt.');

      setNewKey('');
      setNewLabelDe('');
      setNewOptionsJson('');
      setNewType('text');
    } catch (e: any) {
      alert(e?.message || 'Fehler beim Anlegen.');
    }
  }

  function renderControl(a: AttributeDef) {
    const type = (a.input_type || 'text').toLowerCase();

    // ✅ Google Photos (read-only)
    if (isGPhotosDef(a)) {
      const getAnyPhotoValue = () => {
        const vUnd = byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
        if (vUnd !== undefined) return vUnd;
        const vNull = byAttrLang.get(`${a.id}|__`)?.value_json;
        if (vNull !== undefined) return vNull;
        const vLang = byAttrLang.get(`${a.id}|${activeLang}`)?.value_json;
        if (vLang !== undefined) return vLang;
        const first = values.find((v) => v.attribute_id === a.id)?.value_json;
        return first ?? null;
      };

      const cur = getAnyPhotoValue();
      return (
        <div>
          <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 6 }}>read-only</div>
          <PhotosGrid value={cur} />
        </div>
      );
    }

    // wind_profile (UND textarea)
    if (a.key === WIND_PROFILE_KEY) {
      const cur = byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
      return (
        <textarea
          value={toStr(cur)}
          onChange={(e) => setValManual(a, WIND_PROFILE_LANG, e.target.value)}
          style={{
            width: '100%',
            padding: '9px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            minHeight: 84,
          }}
        />
      );
    }

    // LiveWind Station – Mono-Text
    if (isLivewindDef(a)) {
      const raw =
        values.find((v) => v.attribute_id === a.id)?.value_json ??
        byAttrLang.get(`${a.id}|__`)?.value_json ??
        byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
      const cur = extractTextLike(raw);
      return (
        <input
          type="text"
          value={cur}
          onChange={(e) => setValManual(a, null, e.target.value)}
          style={{
            width: '100%',
            padding: '9px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
          }}
        />
      );
    }

    // Boolean immer UND (kein ML-Switch)
    if (type === 'boolean' || type === 'checkbox') {
      const raw =
        byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json ??
        byAttrLang.get(`${a.id}|__`)?.value_json ??
        values.find((v) => v.attribute_id === a.id)?.value_json;
      const checked = normalizeBool(raw);

      return (
        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setValManual(a, WIND_PROFILE_LANG, e.target.checked)}
          />
          <span>Wert setzen</span>
        </label>
      );
    }

    // ML-Text: alle Sprachen gleichzeitig + Übersetzen-Button
    if (isMLTextType(a)) {
      const busy = translateBusyAttr === a.id;

      const commonStyle: React.CSSProperties = {
        width: '100%',
        padding: '9px 10px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
      };

      return (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 10,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => translateFromDe(a)}
              disabled={busy}
              style={{
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: 10,
                background: '#0ea5e9',
                color: '#fff',
              }}
            >
              {busy ? 'Übersetze…' : 'DE → EN/FR/IT/HR'}
            </button>

            <label
              style={{
                display: 'inline-flex',
                gap: 8,
                alignItems: 'center',
                fontSize: 12,
                color: '#334155',
              }}
            >
              <input
                type="checkbox"
                checked={translateOverwrite}
                onChange={(e) => setTranslateOverwrite(e.target.checked)}
              />
              Bestehende Übersetzungen überschreiben
            </label>

            {translateErr && (
              <span style={{ fontSize: 12, color: '#dc2626' }}>{translateErr}</span>
            )}
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {LANGS.map((l) => {
              const raw = byAttrLang.get(`${a.id}|${l}`)?.value_json;
              const val = toStr(normalizeFirstValue(raw));
              const meta = extractMeta(raw);
              const aiBadge = meta && typeof (meta as any).ai === 'boolean' ? (meta as any).ai : null;

              return (
                <div
                  key={`${a.id}:${l}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '70px 1fr',
                    gap: 8,
                    alignItems: 'start',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#64748b', paddingTop: 8 }}>
                    {l.toUpperCase()}
                    {aiBadge === true && (
                            <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#111827', color: '#fff' }} title="AI-generated/translated">AI</span>
                          )}
                    {aiBadge === false && (
                            <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#e5e7eb', color: '#111827' }} title="Manuell editiert">MANUAL</span>
                          )}
                  </div>

                  {type === 'textarea' ? (
                    <textarea
                      style={{ ...commonStyle, minHeight: 72 }}
                      value={val}
                      onChange={(e) => setValManual(a, l, e.target.value)}
                    />
                  ) : (
                    <input
                      style={commonStyle}
                      type="text"
                      value={val}
                      onChange={(e) => setValManual(a, l, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Mono (null/UND)
    const raw =
      byAttrLang.get(`${a.id}|__`)?.value_json ??
      byAttrLang.get(`${a.id}|${WIND_PROFILE_LANG}`)?.value_json;
    const cur = normalizeFirstValue(raw);

    switch (type) {
      case 'number':
        return (
          <input
            type="number"
            value={toNumOrEmpty(cur)}
            onChange={(e) =>
              setValManual(a, null, e.target.value === '' ? null : Number(e.target.value))
            }
          />
        );
      case 'json': {
        const txt = (() => {
          try {
            return JSON.stringify(cur ?? null, null, 2);
          } catch {
            return toStr(cur);
          }
        })();
        return (
          <textarea
            value={txt}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              minHeight: 84,
            }}
            onChange={(e) => {
              const val = e.target.value;
              try {
                setVal(a, null, JSON.parse(val));
              } catch {
                setVal(a, null, val);
              }
            }}
          />
        );
      }
      case 'select': {
        const opts = asOptions(a);
        const val = extractTextLike(cur);
        return (
          <select
            value={val}
            onChange={(e) => setValManual(a, null, e.target.value)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          >
            <option value="">– wählen –</option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>
                {toStr(o.label)}
              </option>
            ))}
          </select>
        );
      }
      case 'url':
      case 'tel':
      case 'email':
      case 'text':
        return (
          <input
            type={a.input_type || 'text'}
            value={extractTextLike(cur)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
            onChange={(e) => setValManual(a, null, e.target.value)}
          />
        );
      case 'textarea':
        return (
          <textarea
            value={extractTextLike(cur)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              minHeight: 84,
            }}
            onChange={(e) => setValManual(a, null, e.target.value)}
          />
        );
      default:
        return (
          <input
            type="text"
            value={extractTextLike(cur)}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
            onChange={(e) => setValManual(a, null, e.target.value)}
          />
        );
    }
  }

  if (loading) {
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 12,
        }}
      >
        Lade Attribute…
      </div>
    );
  }
  if (error) {
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 12,
          color: '#dc2626',
        }}
      >
        {error}
      </div>
    );
  }

  const windInfoAttr = attrs.find((a) => a.key === WIND_INFO_KEY);
  const livewindAttr = attrs.find((a) => a.key === LIVEWIND_KEY || a.id === LIVEWIND_ATTR_ID);

  // LiveWind-Wert für Spezialblock (ID als Text)
  const livewindValue =
    livewindAttr &&
    extractTextLike(
      values.find((v) => v.attribute_id === livewindAttr.id)?.value_json ??
        byAttrLang.get(`${livewindAttr.id}|__`)?.value_json ??
        byAttrLang.get(`${livewindAttr.id}|${WIND_PROFILE_LANG}`)?.value_json ??
        ''
    );

  const badgeText = (a: AttributeDef) => {
    const t = toStr(a.input_type || 'text');
    if (a.key === WIND_PROFILE_KEY) return `${t} · UND`;
    if (isBooleanType(a)) return `${t} · UND`;
    if (isMLTextType(a)) return `${t} · ML`;
    return `${t} · UND`;
  };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Spezieller Block: Wind/Schwell Hinweis */}
      {windInfoAttr && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 600 }}>{labelOf(windInfoAttr) || 'Wind/Schwell Hinweis'}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Hinweistext · mehrsprachig</div>
          </div>
          {renderControl(windInfoAttr)}
          {(windInfoAttr.description_de || windInfoAttr.description_en) && (
            <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
              {toStr(windInfoAttr.description_de || windInfoAttr.description_en)}
            </div>
          )}
        </div>
      )}

      {/* Spezieller Block: LiveWind Station */}
      {livewindAttr && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 600 }}>LiveWind Station</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Meteostat Stations-ID für Live-Windanzeige
            </div>
          </div>
          <input
            type="text"
            value={livewindValue ?? ''}
            onChange={(e) => {
              const id = e.target.value.trim();
              if (livewindSuggestion && livewindSuggestion.id === id) {
                setValManual(livewindAttr, null, { id, name: livewindSuggestion.name ?? null });
              } else {
                setValManual(livewindAttr, null, id);
              }
            }}
            style={{
              width: '100%',
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />

          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            {livewindSuggestLoading && 'Suche passende Meteostat-Station…'}
            {!livewindSuggestLoading && livewindSuggestion && (
              <>
                Vorschlag:&nbsp;<strong>{livewindSuggestion.id}</strong>
                {livewindSuggestion.name ? ` – ${livewindSuggestion.name}` : ''}
                {typeof livewindSuggestion.distance_m === 'number'
                  ? ` (${(livewindSuggestion.distance_m / 1000).toFixed(1)} km entfernt)`
                  : ''}
                <button
                  type="button"
                  onClick={() =>
                    setVal(livewindAttr, null, {
                      id: livewindSuggestion.id,
                      name: livewindSuggestion.name ?? null,
                    })
                  }
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    padding: '3px 8px',
                    borderRadius: 999,
                    border: '1px solid #cbd5e1',
                    background: '#f1f5f9',
                    cursor: 'pointer',
                  }}
                >
                  Vorschlag übernehmen
                </button>
              </>
            )}
            {!livewindSuggestLoading && livewindSuggestError && <span>{livewindSuggestError}</span>}
          </div>
        </div>
      )}

      <h2>Attribute</h2>

      {/* EXISTIERENDES ATTRIBUT HINZUFÜGEN */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Attribut hinzufügen</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={addAttrId === '' ? '' : String(addAttrId)}
            onChange={(e) => setAddAttrId(e.target.value ? Number(e.target.value) : '')}
            style={{
              minWidth: 260,
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          >
            <option value="">– Attribut wählen –</option>
            {addableDefs.map((d) => (
              <option key={d.id} value={d.id}>
                {labelOf(d)}
              </option>
            ))}
          </select>
          <div style={{ flex: 1, minWidth: 260 }}>
            {renderAddValueInput(addAttrId === '' ? undefined : attrs.find((a) => a.id === addAttrId))}
          </div>
          <button
            onClick={handleAddExisting}
            disabled={addAttrId === ''}
            style={{
              padding: '10px 14px',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              background: '#0ea5e9',
              color: '#fff',
            }}
            type="button"
          >
            Hinzufügen
          </button>
        </div>
      </div>

      {/* LISTE & EDIT */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {attrs
          .filter((a) => a.key !== WIND_INFO_KEY) // nicht doppelt rendern
          .map((a) => (
            <div
              key={a.id}
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 600 }}>{labelOf(a)}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{badgeText(a)}</div>
              </div>
              {renderControl(a)}
              {(a.description_de ||
                a.description_en ||
                a.description_it ||
                a.description_fr ||
                a.description_hr) && (
                <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                  {toStr(
                    (activeLang === 'de' ? a.description_de : null) ||
                      (activeLang === 'en' ? a.description_en : null) ||
                      (activeLang === 'it' ? a.description_it : null) ||
                      (activeLang === 'fr' ? a.description_fr : null) ||
                      (activeLang === 'hr' ? a.description_hr : null) ||
                      a.description_de ||
                      a.description_en
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            padding: '10px 14px',
            border: '1px solid #cbd5e1',
            borderRadius: 10,
            background: '#0ea5e9',
            color: '#fff',
          }}
        >
          {saving ? 'Speichere…' : 'Attribute speichern'}
        </button>
      </div>

      {/* NEUES ATTRIBUT ANLEGEN */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 12,
          marginTop: 16,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Neues Attribut anlegen</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '240px 200px 1fr auto',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <input
            placeholder="key (z. B. wind_n)"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            style={{
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as any)}
            style={{
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          >
            <option value="text">text</option>
            <option value="number">number</option>
            <option value="boolean">boolean</option>
            <option value="select">select</option>
            <option value="multiselect">multiselect</option>
            <option value="json">json</option>
          </select>
          <input
            placeholder="Label (deutsch)"
            value={newLabelDe}
            onChange={(e) => setNewLabelDe(e.target.value)}
            style={{
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />
          <button
            onClick={handleCreateNew}
            style={{
              padding: '10px 14px',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              background: '#0ea5e9',
              color: '#fff',
            }}
            type="button"
          >
            Anlegen
          </button>
        </div>
        <div style={{ marginTop: 8 }}>
          <label
            style={{
              display: 'block',
              fontSize: 12,
              color: '#64748b',
              marginBottom: 4,
            }}
          >
            Optionen (JSON, optional)
          </label>
          <textarea
            placeholder='z.B. [{"value":"yes","label_de":"Ja"},{"value":"no","label_de":"Nein"}]'
            value={newOptionsJson}
            onChange={(e) => setNewOptionsJson(e.target.value)}
            style={{
              width: '100%',
              minHeight: 80,
              padding: '9px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
            }}
          />
        </div>
      </div>
    </div>
  );
}
